import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink, Pencil } from "lucide-react";
import PartEditDialog from "@/components/PartEditDialog";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell,
} from "recharts";

const STATUS_COLOR = {
    "Lead": "#52525B", "RFQ": "#002FA7", "Feasibility": "#5B6FE0",
    "Quotation Submitted": "#9C27B0", "Sample Submission": "#FFB703",
    "Technical/Commercial Approval": "#FF9800", "PO": "#2A9D8F", "Loss/Regret": "#E63946",
};
const SEGMENT_COLORS = { "Auto": "#002FA7", "Non-Auto": "#9C27B0", "Domestic": "#FFB703", "Export": "#2A9D8F", "-": "#a1a1aa" };

const fmtCr = (v) => {
    // Indian formatting: crore / lakh
    const n = Number(v) || 0;
    if (Math.abs(n) >= 10000000) return `₹ ${(n / 10000000).toFixed(2)} Cr`;
    if (Math.abs(n) >= 100000) return `₹ ${(n / 100000).toFixed(2)} L`;
    return `₹ ${n.toLocaleString()}`;
};

export default function SalesDashboard() {
    const [data, setData] = useState(null);
    const [search, setSearch] = useState("");
    const [editPart, setEditPart] = useState(null);
    const navigate = useNavigate();

    const reload = () => api.get("/sales/dashboard").then((r) => setData(r.data));
    useEffect(() => { reload(); }, []);

    const openEdit = async (partId) => {
        const res = await api.get(`/parts/${partId}`);
        setEditPart(res.data);
    };

    const filteredRows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data.rows;
        return data.rows.filter((r) =>
            (r.part_name || "").toLowerCase().includes(q) ||
            (r.customer_name || "").toLowerCase().includes(q)
        );
    }, [data, search]);

    if (!data) return <div className="p-8 text-zinc-500">Loading…</div>;

    const { totals, by_segment, by_status, by_customer } = data;

    return (
        <div>
            <PageHeader
                eyebrow="Sales"
                title="Sales Projection Dashboard"
                subtitle={`Across ${totals.parts} part${totals.parts === 1 ? "" : "s"}. Numbers use the derived price from each part's selected draft.`}
            />

            <div className="px-8 py-6 space-y-6">
                {/* Top KPI strip */}
                <div className="grid grid-cols-4 gap-4" data-testid="sales-kpi-strip">
                    <KPI label="Total Projection (Annual)" value={fmtCr(totals.total_projection)} hint="Σ (annual_volume × derived price) for every non-child part (any status, excludes Loss/Regret)." accent="#002FA7" testid="kpi-projection" />
                    <KPI label="Confirmed (PO) Annual" value={fmtCr(totals.confirmed_revenue)} hint="Σ (po_volume × sales_price) for parts where status = PO. Falls back to annual_volume × derived price when po_volume / sales_price not entered." accent="#2A9D8F" testid="kpi-confirmed" />
                    <KPI label="In Pipeline" value={fmtCr(totals.pipeline_revenue)} hint="Total Projection − Confirmed PO − Loss/Regret. Active opportunities still being chased." accent="#FFB703" testid="kpi-pipeline" />
                    <KPI label="Lost / Regretted" value={fmtCr(totals.loss_revenue)} hint="Σ (annual_volume × derived price) for parts marked Loss / Regret." accent="#E63946" testid="kpi-loss" />
                </div>

                {/* Charts row */}
                <div className="grid grid-cols-2 gap-6">
                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">By Segment</div>
                        <h3 className="text-xl font-bold tracking-tight mb-4">Annual Revenue by Segment</h3>
                        <div style={{ height: 280 }}>
                            <ResponsiveContainer>
                                <BarChart data={by_segment}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                    <XAxis dataKey="segment" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString()} />
                                    <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString()}`} />
                                    <Bar dataKey="annual_revenue" name="Annual Revenue">
                                        {by_segment.map((d, i) => <Cell key={i} fill={SEGMENT_COLORS[d.segment] || "#264653"} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">By Status</div>
                        <h3 className="text-xl font-bold tracking-tight mb-4">Annual Revenue by Stage</h3>
                        <div style={{ height: 280 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={by_status}
                                        dataKey="annual_revenue"
                                        nameKey="status"
                                        cx="50%" cy="50%" outerRadius={95}
                                        label={({ status, percent }) => `${status}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {by_status.map((d, i) => <Cell key={i} fill={STATUS_COLOR[d.status] || "#a1a1aa"} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString()}`} />
                                    <Legend formatter={(value) => value === "annual_revenue" ? "Annual Revenue" : value} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Top Customers */}
                <div className="border border-zinc-200">
                    <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                        <div className="text-sm font-semibold tracking-tight">Top Customers</div>
                        <div className="text-xs text-zinc-500 font-mono">Ranked by annual revenue</div>
                    </div>
                    <div className="p-4">
                        <table className="w-full sheet-table" data-testid="customers-table">
                            <thead><tr><th>Customer</th><th>Parts</th><th className="text-right">Annual (₹)</th><th className="text-right">Confirmed (₹)</th><th className="text-right">Pipeline (₹)</th><th className="text-right">Lifetime (₹)</th></tr></thead>
                            <tbody>
                                {by_customer.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center text-zinc-400 italic py-6">No data yet.</td></tr>
                                ) : by_customer.map((c) => {
                                    const pipeline = c.annual_revenue - c.confirmed_revenue;
                                    return (
                                        <tr key={c.customer} data-testid={`customer-row-${c.customer}`}>
                                            <td className="font-medium">{c.customer}</td>
                                            <td className="font-mono">{c.parts}</td>
                                            <td className="text-right font-mono">{c.annual_revenue.toLocaleString()}</td>
                                            <td className="text-right font-mono text-[#2A9D8F]">{c.confirmed_revenue.toLocaleString()}</td>
                                            <td className="text-right font-mono text-[#FFB703]">{pipeline.toLocaleString()}</td>
                                            <td className="text-right font-mono">{c.lifetime_revenue.toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Parts list */}
                <div className="border border-zinc-200">
                    <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200 gap-3">
                        <div className="text-sm font-semibold tracking-tight">Parts & Projections</div>
                        <div className="relative max-w-xs flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input data-testid="sales-search" placeholder="Search part or customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-none h-8" />
                        </div>
                    </div>
                    <div className="p-4">
                        <table className="w-full sheet-table" data-testid="sales-rows-table">
                            <thead><tr><th>Part</th><th>Customer</th><th>Segment</th><th>Status</th><th className="text-right">Vol/yr</th><th className="text-right">Price (₹)</th><th className="text-right">Annual Rev (₹)</th><th className="text-right">Lifetime Rev (₹)</th><th></th></tr></thead>
                            <tbody>
                                {filteredRows.map((r) => (
                                    <tr key={r.part_id} data-testid={`sales-row-${r.part_id}`}>
                                        <td className="font-medium">{r.part_name}</td>
                                        <td>{r.customer_name}</td>
                                        <td>
                                            <span className="tag-pill" style={{ borderColor: SEGMENT_COLORS[r.segment] || "#09090b" }}>
                                                {r.segment}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2 text-xs">
                                                <div className="w-2 h-2" style={{ background: STATUS_COLOR[r.status] || "#a1a1aa" }} />
                                                {r.status}
                                            </div>
                                        </td>
                                        <td className="text-right font-mono">{r.annual_volume.toLocaleString()}</td>
                                        <td className="text-right font-mono">{r.derived_price.toLocaleString()}</td>
                                        <td className="text-right font-mono font-semibold">{r.annual_revenue.toLocaleString()}</td>
                                        <td className="text-right font-mono text-zinc-500">{r.lifetime_revenue.toLocaleString()}</td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => openEdit(r.part_id)}
                                                className="text-zinc-400 hover:text-[#002FA7] mr-2"
                                                data-testid={`edit-sales-part-${r.part_id}`}
                                                title="Edit part"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                onClick={() => navigate(`/parts/${r.part_id}/proposal`)}
                                                className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider"
                                                data-testid={`open-proposal-${r.part_id}`}
                                            >
                                                Proposal <ExternalLink size={10} className="inline" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredRows.length === 0 && (
                                    <tr><td colSpan={9} className="text-center text-zinc-400 italic py-6">No parts match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Fallback button area to use Button import */}
            <div className="hidden"><Button /></div>

            <PartEditDialog
                open={!!editPart}
                onOpenChange={(v) => { if (!v) setEditPart(null); }}
                part={editPart}
                onSaved={() => { setEditPart(null); reload(); }}
            />
        </div>
    );
}

function KPI({ label, value, accent, testid, hint }) {
    return (
        <div className="border border-zinc-200 p-5" data-testid={testid} title={hint || ""}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
            <div className="text-3xl font-bold font-mono mt-2" style={{ color: accent }}>{value}</div>
            {hint && <div className="text-[10px] text-zinc-400 font-mono mt-2 leading-snug">{hint}</div>}
        </div>
    );
}
